/**
 * 工作流节点组件导出
 *
 * 统一导出所有节点组件和 nodeTypes 映射
 */

import type { NodeTypes } from '@xyflow/react';

// 导出所有节点组件
export { CommonHandle, InputHandle, OutputHandle } from './CommonHandle';
export type { CommonHandleProps } from './CommonHandle';

export { NodeWrapper } from './NodeWrapper';
export type { NodeWrapperProps } from './NodeWrapper';

export { StartNode } from './StartNode';
export { AgentNode } from './AgentNode';
export { LoopNode } from './LoopNode';
export { ConditionNode } from './ConditionNode';
export { InfoNode } from './InfoNode';
export { NoteNode } from './NoteNode';

// 导入节点组件用于 nodeTypes 映射
import { StartNode } from './StartNode';
import { AgentNode } from './AgentNode';
import { LoopNode } from './LoopNode';
import { ConditionNode } from './ConditionNode';
import { InfoNode } from './InfoNode';
import { NoteNode } from './NoteNode';

/**
 * 节点类型映射
 *
 * 用于 ReactFlow 的 nodeTypes 属性
 * key 对应节点 type，value 对应组件
 */
export const nodeTypes: NodeTypes = {
  start: StartNode,
  agent: AgentNode,
  loop: LoopNode,
  condition: ConditionNode,
  info: InfoNode,
  note: NoteNode,
};

/**
 * 节点类型显示名称
 */
export const nodeTypeNames: Record<string, string> = {
  start: '开始节点',
  agent: 'Agent 节点',
  loop: '循环容器',
  condition: '条件分支',
  info: '信息提取',
  note: '注释',
};

/**
 * 节点类型图标
 */
export const nodeTypeIcons: Record<string, React.ReactNode> = {
  start: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  agent: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  loop: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  condition: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  note: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
};

/**
 * 节点类型颜色
 */
export const nodeTypeColors: Record<string, { bg: string; text: string; border: string }> = {
  start: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-300' },
  agent: { bg: 'bg-paper-50', text: 'text-paper-700', border: 'border-paper-300' },
  loop: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-300' },
  condition: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-300' },
  info: { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-300' },
  note: { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-300' },
};

/**
 * 默认节点配置
 */
export const defaultNodeConfigs = {
  start: {
    label: '开始',
    task_prompt: '',
  },
  agent: {
    label: 'Agent',
    agent_id: null,
    enabled_tools: [],
  },
  loop: {
    label: '循环容器',
    chapter_start: 1,
    chapter_end: 1,
    loop_feedback: false,
    children: [],
  },
  condition: {
    label: '条件分支',
    branches: [],
  },
  info: {
    label: '信息提取',
    prompt: '',
    fields: [],
  },
  note: {
    label: '注释',
    content: '',
    color: 'default',
  },
};

export default nodeTypes;